import React, { useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { faAsterisk } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AttachmentIcon from './attachment_icon';
import DeleteModal from './delete_modal';
import Dropdown from 'components/dropdown';
import ReminderInfo from 'bundles/apex/reminders/info';
import Status from './status';
import Parent from './parent';
import TaskFormModal from 'bundles/apex/tasks/task_modal';
import Tooltip from 'components/tooltip';
import UserIconList from 'components/user_icon_list';
import { formatDate as formatApexDate } from 'bundles/apex/dates/helpers';
import { formatDate, DAY_MONTH_FORMAT } from 'helpers/formatters/date_time_formats';

import './styles.scss';

export default function Task({
  addTaskAttachment,
  adminedOfficeIds = [],
  apexAdmin,
  attachmentExceptions,
  attachmentsPath,
  canDeleteAttachments,
  checkImmediateRemindersPath,
  currentParent,
  deal,
  deleteTaskAttachment,
  editable = true,
  editing,
  fields,
  options,
  requiredTasksEnabled,
  selected,
  showDealColumn,
  showParents,
  showType,
  task,
  userId,
  onCreate,
  onDelete,
  onSelectionToggle,
  onUpdate,
}) {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const adminForDeal = apexAdmin || adminedOfficeIds?.includes(deal?.officeId);

  const reminders = task.reminders.filter(reminder => reminder.userId === userId);

  return (
    <React.Fragment>
      {editable &&
        <TaskFormModal
          checkImmediateRemindersPath={checkImmediateRemindersPath}
          createTask={onCreate}
          formDependenciesPath={task.formDependenciesPath}
          hideStatusButton
          options={options}
          reminders={reminders}
          show={showModal}
          showType={showType}
          task={task}
          updateTask={onUpdate}
          onHide={() => setShowModal(false)}
          onSave={() => setShowModal(false)}
        />
      }

      <DeleteModal
        show={showDeleteModal}
        onDelete={() => onDelete(task)}
        onHide={() => setShowDeleteModal(false)}
      />

      <tr aria-label="task" className="planner-row">
        {editing && (
          <td className="text-center">
            <input
              aria-label="task checkbox"
              checked={selected || false}
              type="checkbox"
              onChange={() => onSelectionToggle(task)}
            />
          </td>
        )}
        <td className="task-complete">
          <Status
            addTaskAttachment={addTaskAttachment}
            attachmentExceptions={attachmentExceptions}
            attachmentsPath={attachmentsPath}
            deleteTaskAttachment={deleteTaskAttachment}
            task={task}
            onUpdate={onUpdate}
          />
        </td>
        <td className={classNames({ 'reminder-info-cell': reminders.length > 0 })}>
          {reminders.length ? (
            <ReminderInfo
              referenceDate={task?.deadline.date}
              reminders={reminders}
              onClick={() => setShowModal(true)}
            />
          ) : <div />}
        </td>
        <td className="planner-table-date">
          <div>
            <div className="d-flex align-items-center">
              {(task.deadline && task.deadline.date)
                ? formatDate(task.deadline.date, DAY_MONTH_FORMAT).toUpperCase()
                : 'TBD'
              }
            </div>
            {task.deadline && !task.deadline.static &&
              <div className="task-deadline-relative mt-1">
                {formatApexDate(deal, fields, task.deadline, false)}
              </div>
            }
          </div>
        </td>
        {showDealColumn &&
          <td>
            <div><a href={`${deal.path}/planner`}>{deal.displayName}</a></div>
          </td>
        }
        <td>
          <div>
            <div className="d-flex flex-row align-items-start gap-1">
              {task.name}
              {task.required &&
                <Tooltip content="Required for voucher submission">
                  <FontAwesomeIcon className="text-danger fs-xs" icon={faAsterisk} />
                </Tooltip>
              }
            </div>
          </div>
        </td>
        <td>
          <AttachmentIcon
            addTaskAttachment={addTaskAttachment}
            attachmentExceptions={attachmentExceptions}
            attachmentsPath={attachmentsPath}
            canDeleteAttachments={canDeleteAttachments}
            deleteTaskAttachment={deleteTaskAttachment}
            forUserId={userId}
            task={task}
            onUpdate={onUpdate}
          />
        </td>
        <td>
          <div>
            {task.assignee &&
              <UserIconList size="lg" users={[task.assignee]} />
            }
          </div>
        </td>

        {showParents &&
          <td>
            <Parent
              currentParent={currentParent}
              options={options}
              task={task}
            />
          </td>
        }

        <td>
          <div>
            <Dropdown>
              <Dropdown.Item
                isEdit
                onClick={() => setShowModal(true)}
              />
              <Dropdown.Item
                disabled={requiredTasksEnabled && task.required && !adminForDeal}
                disabledMessage="Required tasks cannot be deleted"
                isDelete
                onClick={() => setShowDeleteModal(true)}
              />
            </Dropdown>
          </div>
        </td>
      </tr>
    </React.Fragment>
  );
}

Task.propTypes = {
  addTaskAttachment: PropTypes.func.isRequired,
  adminedOfficeIds: PropTypes.arrayOf(PropTypes.number),
  apexAdmin: PropTypes.bool,
  attachmentExceptions: PropTypes.bool,
  attachmentsPath: PropTypes.string,
  canDeleteAttachments: PropTypes.bool.isRequired,
  checkImmediateRemindersPath: PropTypes.string,
  currentParent: PropTypes.object,
  deal: PropTypes.object,
  deleteTaskAttachment: PropTypes.func.isRequired,
  editable: PropTypes.bool,
  editing: PropTypes.bool,
  fields: PropTypes.object,
  options: PropTypes.object,
  requiredTasksEnabled: PropTypes.bool,
  selected: PropTypes.bool,
  showDealColumn: PropTypes.bool,
  showParents: PropTypes.bool,
  showType: PropTypes.bool,
  task: PropTypes.shape({
    formDependenciesPath: PropTypes.string,
  }).isRequired,
  userId: PropTypes.number,
  onCreate: PropTypes.func,
  onDelete: PropTypes.func,
  onSelectionToggle: PropTypes.func,
  onUpdate: PropTypes.func,
};
